import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/shared/ui/button';
import { Dialog } from '@/shared/ui/dialog/Dialog';

import { ListPage } from './ListPage';
import type { ListPageConfig } from './model/types';

/**
 * Výběrový (picker) režim ListPage v modálním okně (anatomie 05 §7) — tatáž šablona, jen
 * řádky se vybírají a patička nese „N vybráno · Zrušit · Potvrdit výběr". Vstup = aktuální
 * výběr (id), výstup = potvrzená množina id. Bez draweru, bulk lišty a akcí řádku.
 */
export function ListPagePickerModal<TItem, TStats>({
  config,
  open,
  onClose,
  initialSelectedIds,
  onConfirm,
}: {
  config: ListPageConfig<TItem, TStats>;
  open: boolean;
  onClose: () => void;
  initialSelectedIds: (string | number)[];
  onConfirm: (ids: (string | number)[]) => void;
}) {
  const { t } = useTranslation('listPage');
  const [selectedIds, setSelectedIds] = useState<(string | number)[]>(initialSelectedIds);

  useEffect(() => {
    if (open) {
      setSelectedIds(initialSelectedIds);
    }
  }, [open, initialSelectedIds]);

  if (!open) {
    return null;
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t('picker.title')}
      width="xl"
      align="top"
      headerVariant="primary"
      closeOnBackdrop={false}
      bodyPadded={false}
      bodyClassName="flex flex-col h-[70vh]"
      footer={
        <div className="gap-phx-4 flex w-full items-center">
          <span className="text-phx-md text-phx-text-2">
            <b className="tabular-nums">{selectedIds.length}</b>{' '}
            {t('picker.selected', { count: selectedIds.length })}
          </span>
          <span className="gap-phx-3 ml-auto flex">
            <Button variant="outline" onClick={onClose}>
              {t('picker.cancel')}
            </Button>
            <Button
              variant="primary"
              disabled={selectedIds.length === 0}
              onClick={() => {
                onConfirm(selectedIds);
                onClose();
              }}
            >
              {t('picker.confirm')}
            </Button>
          </span>
        </div>
      }
    >
      <ListPage
        config={config}
        picker={{ initialSelection: initialSelectedIds, onSelectionChange: setSelectedIds }}
      />
    </Dialog>
  );
}
